import type { JSX } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { formatDate } from '@/lib/dates';
import { Users } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { PlatformIcon } from '@/components/PlatformIcon';
import type { ContactedNotReplied } from '@/api/types';

/**
 * Creators who were messaged but haven't replied ("Messaged · no reply").
 * A message isn't a log: nothing is owed until the creator replies and an
 * outreacher logs it.
 *
 * Rendered as its own table rather than squeezed into the invite columns:
 * these rows have no invite, so status / owed / clips / verification would all
 * be permanently blank, and a table of empty cells reads as broken data rather
 * than as a different kind of row.
 *
 * `showWho` is off for the outreacher's own view — they see their own outreach
 * only, so a column repeating their name in every row is noise.
 */
export function ContactedTable({
  rows,
  showWho,
}: {
  rows: ContactedNotReplied[];
  showWho: boolean;
}): JSX.Element {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Users className="h-6 w-6" />}
        title="Nobody's waiting on a reply."
        hint="Creators who were messaged show up here until someone logs their reply."
      />
    );
  }

  return (
    <div className="table-frame relative overflow-x-auto">
      <table className="responsive-stack w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-medium">Creator</th>
            {showWho && <th className="px-3 py-2 font-medium">Messaged by</th>}
            <th className="px-3 py-2 font-medium">First message</th>
            <th className="px-3 py-2 font-medium">Last message</th>
            <th className="px-3 py-2 text-right font-medium">Messages</th>
            <th className="px-3 py-2 font-medium">Waiting</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const waitingDays = Math.floor(
              (Date.now() - new Date(r.lastContactedAt).getTime()) / 86_400_000,
            );
            // A week of silence is the point where chasing is more useful than
            // waiting, so it gets visual weight.
            const stale = waitingDays >= 7;
            return (
              <tr key={r.publicId} className="border-b last:border-0">
                <td className="px-3 py-2">
                  <span className="flex items-center gap-2 font-medium">
                    <PlatformIcon
                      platform={r.channel}
                      size={14}
                      className="text-muted-foreground"
                    />
                    @{r.targetIdentifier}
                  </span>
                </td>
                {showWho && (
                  <td className="px-3 py-2 text-muted-foreground" data-label="Messaged by">
                    <span title={r.contactedBy.map((c) => c.inviterName).join(', ')}>
                      {r.contactedBy[0]?.inviterName ?? '—'}
                      {r.outreacherCount > 1 && (
                        <span className="ml-1 rounded-full border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                          +{r.outreacherCount - 1} more
                        </span>
                      )}
                    </span>
                  </td>
                )}
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground" data-label="First message">
                  {formatDate(r.firstContactedAt)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground" data-label="Last message">
                  {formatDate(r.lastContactedAt)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums" data-label="Messages">{r.attemptCount}</td>
                <td
                  data-label="Waiting"
                  className={`whitespace-nowrap px-3 py-2 ${
                    stale ? 'font-medium text-warning' : 'text-muted-foreground'
                  }`}
                >
                  {formatDistanceToNow(new Date(r.lastContactedAt), { addSuffix: false })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
